import clsx from 'clsx';
import React from 'react';
import { useEffect } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import insertScript from '@dashboard/lib/insertScript';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import { isProduction } from '@dashboard/services/env';

import Meta from '@dashboard/components/Meta';
import Banner from '@dashboard/components/Banner';
import AddStoreNameDialog from '@dashboard/components/AddStoreNameDialog';
import ConnectStripeDialog from '@dashboard/components/ConnectStripeDialog';
import UpgradeToYearlyDialog from '@dashboard/components/UpgradeToYearlyDialog';

import Navigation from './Navigation';
import AdminGhostingDialog from '@/dashboard/admin/components/AdminGhostingDialog';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { injectTrackingScript } from '@/dashboard/lib/scriptInjector';

const MainLayout = ({ isFocused, children }) => {
  useTracking();
  const location = useLocation();
  const { user, seller, isAuth, isAdmin, isConnectedToStripe } = useAuth();
  const { adminGhostingSellerId } = useAdminGhosting();

  const [identifiedCrispUser, setIdentifiedCrispUser] = React.useState(false);
  const [identifiedCrispSeller, setIdentifiedCrispSeller] = React.useState(false);

  // track pageviews
  useEffect(() => {
    tracking.trackPageview();
  }, [location]);

  // load rewardful
  useEffect(() => {
    function loadRewardful() {
      injectTrackingScript(
        `
        <script>(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');</script>
        <script async src='https://r.wdfl.co/rw.js' data-rewardful='e2e9f1'></script>
      `,
      );
    }

    if (isProduction) {
      loadRewardful();
    }
  }, []);

  // load google ads
  useEffect(() => {
    function loadGoogleAds() {
      injectTrackingScript(
        `
        <!-- Google tag (gtag.js) -->
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-ZQ3SPC315S"></script>
        <script>
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-ZQ3SPC315S', {
            send_page_view: false,
          });

          gtag('config', 'AW-16833295745', {
            enhanced_conversions: true,
            allow_enhanced_conversions: true,
            send_page_view: false
          });
        </script>
      `,
      );
    }

    if (isProduction) {
      loadGoogleAds();
    }
  }, []);

  // load facebook pixel
  useEffect(() => {
    function loadFacebookPixel() {
      insertScript(`
        <!-- Meta Pixel Code -->
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '976736210989373');
        fbq('track', 'PageView');
        <!-- End Meta Pixel Code -->
      `);
    }

    if (isProduction) {
      loadFacebookPixel();
    }
  }, []);

  useEffect(() => {
    function loadLinkedInInsight() {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        _linkedin_partner_id = "7177180";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);

        (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
      `;
      document.body.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `
        <img
          height="1"
          width="1"
          style="display:none;"
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=7177180&fmt=gif"
        />
      `;
      document.body.appendChild(noscript);
    }

    if (isProduction) {
      loadLinkedInInsight();
    }
  }, []);

  useEffect(() => {
    function loadCrisp() {
      insertScript(`
        window.$crisp=[];
        window.CRISP_WEBSITE_ID="abe8e9e1-02b6-4715-b289-7573fd280b36";

        (function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
      `);
    }

    if (!window.$crisp) {
      loadCrisp();
    }
  }, []);

  useEffect(() => {
    function identifyCrispUser() {
      if (identifiedCrispUser || !user?._id) {
        return;
      }

      if (window.$crisp) {
        insertScript(`(function() {
          window.$crisp.push(["set", "user:email", ["${user.email}"]]);
          window.$crisp.push(["set", "user:nickname", ["${user.name}"]]);
        })();`);

        setIdentifiedCrispUser(true);
      }
    }

    identifyCrispUser();
  }, [user, identifiedCrispUser]);

  useEffect(() => {
    function identifyCrispSeller() {
      if (identifiedCrispSeller || !seller?._id) {
        return;
      }

      if (window.$crisp) {
        insertScript(`(function() {
          window.$crisp.push(["set", "session:data", [
            [
              ["seller_id", "${seller?._id}"],
              ["subscription_status", "${seller.subscriptionStatus}"],
              ["plan_handle", "${seller.plan?.handle}"],
            ]
          ]]);

          // track segments
          window.$crisp.push([
            "set",
            "session:segments",
            [
              [
                "${seller.subscriptionStatus}", // trialing, active, past_due etc
                "${seller.plan?.handle}", // launch, grow, scale
              ],
            ],
            true // true to replace existing segments
          ]);
        })();`);

        setIdentifiedCrispSeller(true);
      }
    }

    identifyCrispSeller();
  }, [seller, identifiedCrispSeller]);

  const showAddStoreNameDialog = seller._id && !seller.displayName && !isAdmin;

  const isPageDetail =
    location.pathname.includes('/pages/') &&
    !location.pathname.includes('/checkouts') &&
    !location.pathname.includes('/forms');

  return (
    <>
      {!isConnectedToStripe && !isPageDetail && user.onboardingStepsClosed && (
        <ConnectStripeDialog
          trigger={
            <div className="cursor-pointer">
              <Banner>
                <Banner.Message>Connect your Stripe account to accept payments</Banner.Message>
                <Banner.Action>
                  <ArrowRightIcon className="h-4 w-4 text-white mt-0.5" aria-hidden="true" />
                </Banner.Action>
              </Banner>
            </div>
          }
        />
      )}

      {showAddStoreNameDialog && <AddStoreNameDialog isOpen onClose={() => {}} />}

      <UpgradeToYearlyDialog />

      <div
        className={clsx('flex flex-row items-start mx-auto')}
        style={{
          maxWidth: '2560px',
        }}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
              body {
                overscroll-behavior-y: none;
                background: #fff;
              }`,
          }}
        />

        <Meta
          meta={{
            description:
              'Checkout pages and forms to sell your products and services. Installed in minutes without coding. Sell products, subscriptions, digital downloads, featured listings, event tickets and more.',
            url: 'https://checkoutpage.com',
            image: 'https://checkoutpage.com/assets/media-card.png',
            title: 'Checkout Page',
          }}
        />

        {!isFocused && <Navigation isLogged={isAuth} user={user} />}

        <div
          className={clsx(`w-full bg-white relative`, {
            'md:pl-[260px]': !isFocused,
            'mt-12 md:mt-0': !isPageDetail,
          })}
        >
          {children}
        </div>
      </div>

      {isAdmin && adminGhostingSellerId && <AdminGhostingDialog sellerId={adminGhostingSellerId} />}
    </>
  );
};

export default MainLayout;
